import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../../core/rootHooks';
import { RootState } from '../../../../../core/store';
import { ICohortVessel } from '../../../../common/cohort/cohortTypes';
import CohortDefinition from '../../../../common/cohort/components/characteristic/CohortDefinition';
import { TVoyages } from '../../../kpiTypes';
import {
	selectCountsOfGeneralSeaAreasForCohort,
	selectCountsOfGeneralSeaAreasForVessel,
	selectSeaAreasCohortLoading,
	selectSeaAreasVesselLoading,
} from '../../../kpiSelectors';
import VoyagesOfOriginsChart from '../../deployment/voyages/VoyagesOfOriginsChart';
import GeneralSeaAreasChart from './GeneralSeaAreasChart';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';

interface IDeploymentProps {
	vessel: ICohortVessel;
	minSize: number;
	maxSize: number;
	minAge: number;
	maxAge: number;
	cohortVessels: ICohortVessel[];
	vesselVoyages: TVoyages | null;
	cohortVesselVoyages: TVoyages;
}

const Deployment: React.FC<IDeploymentProps> = ({ ...props }) => {
	const countsOfGeneralSeaAreasVessel = useAppSelector((state: RootState) =>
		selectCountsOfGeneralSeaAreasForVessel(state, props.vessel.imoNumber!),
	);
	const seaAreasVesselLoading = useAppSelector((state: RootState) => selectSeaAreasVesselLoading(state, props.vessel.imoNumber!));

	const countsOfGeneralSeaAreasCohort = useAppSelector(selectCountsOfGeneralSeaAreasForCohort);
	const seaAreasCohortLoading = useAppSelector(selectSeaAreasCohortLoading);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	const cohortVesselInfo = props.cohortVessels.length === 1 ? props.cohortVessels[0] : props.vessel;

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-row gap-4 w-full h-full">
			<div className="flex flex-col gap-4 w-full">
				<div className="flex-none">
					<CohortDefinition
						title={props.vessel.name}
						shipsNumber={props.cohortVessels.length}
						size={props.vessel.size}
						sizeUnit={props.vessel.sizeUnit}
						age={props.vessel.age}
						isCohort={false}
					/>
				</div>
				<div className="flex flex-col h-full gap-4">
					<div className="basis-1/2 border border-msi-darkgrey-color-300 rounded">
						{seaAreasVesselLoading ? (
							<LoadingSpinner size="md" spinnerClassName="p-2" />
						) : (
							<ExpandableContainer panelOffsets={panelOffset} title="By Sea Area">
								<GeneralSeaAreasChart data={countsOfGeneralSeaAreasVessel} />
							</ExpandableContainer>
						)}
					</div>
					<div className="basis-1/2 border border-msi-darkgrey-color-300 rounded">
						{!props.vesselVoyages || props.vesselVoyages.loading ? (
							<LoadingSpinner size="md" spinnerClassName="p-2" />
						) : (
							<ExpandableContainer panelOffsets={panelOffset} title="By Voyage">
								<VoyagesOfOriginsChart imoNumber={props.vessel.imoNumber} vesselVoyages={props.vesselVoyages} horizontal />
							</ExpandableContainer>
						)}
					</div>
				</div>
			</div>
			<div className="flex flex-col gap-4 w-full">
				<div className="flex-none">
					<CohortDefinition
						title="Cohort (Average By Vessel)"
						minSize={props.minSize}
						maxSize={props.maxSize}
						shipsNumber={props.cohortVessels.length}
						size={cohortVesselInfo.size}
						sizeUnit={cohortVesselInfo.sizeUnit}
						age={cohortVesselInfo.age}
						minAge={props.minAge}
						maxAge={props.maxAge}
						isCohort={true}
					/>
				</div>
				<div className="flex flex-col h-full gap-4">
					<div className="basis-1/2 border border-msi-darkgrey-color-300 rounded">
						{seaAreasCohortLoading ? (
							<LoadingSpinner size="md" spinnerClassName="p-2" />
						) : (
							<ExpandableContainer panelOffsets={panelOffset} title="By Sea Area (Cohort)">
								<GeneralSeaAreasChart data={countsOfGeneralSeaAreasCohort} />
							</ExpandableContainer>
						)}
					</div>
					<div className="basis-1/2 border border-msi-darkgrey-color-300 rounded">
						{!props.cohortVesselVoyages || props.cohortVesselVoyages.loading ? (
							<LoadingSpinner size="md" spinnerClassName="p-2" />
						) : (
							<ExpandableContainer panelOffsets={panelOffset} title="By Voyage (Cohort)">
								<VoyagesOfOriginsChart imoNumber={props.vessel.imoNumber} vesselVoyages={props.cohortVesselVoyages} horizontal />
							</ExpandableContainer>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Deployment;
